import { baseApi } from './baseApi';
import {
  ShiftClosingDto,
  ShiftClosingListDto,
  CreateShiftClosingDto,
  ReviewShiftClosingDto,
  ShiftClosingValidationDto,
  ShiftClosingReviewValidationDto,
} from '../types/cashRegister';

export const shiftClosingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get a specific shift closing by ID
    getShiftClosing: builder.query<ShiftClosingDto, string>({
      query: (id) => `/shift-closings/${id}`,
      providesTags: (result, error, id) => [
        { type: 'ShiftClosing' as const, id },
      ],
    }),

    // Get all shift closings for a specific cash register
    getShiftClosingsByCashRegister: builder.query<ShiftClosingDto[], string>({
      query: (cashRegisterId) => `/shift-closings/cash-register/${cashRegisterId}`,
      providesTags: (result) => [
        { type: 'ShiftClosing' as const, id: 'LIST' },
        ...(result || []).map(({ id }) => ({ type: 'ShiftClosing' as const, id })),
      ],
    }),

    // Get all pending shift closings for the current restaurant (for admin review)
    getPendingShiftClosings: builder.query<ShiftClosingListDto[], void>({
      query: () => `/shift-closings/pending`,
      providesTags: (result) => [
        { type: 'ShiftClosing' as const, id: 'PENDING' },
        ...(result || []).map(({ id }) => ({ type: 'ShiftClosing' as const, id })),
      ],
    }),

    // Get pending shift closings count (for badge display)
    getPendingShiftClosingsCount: builder.query<{ count: number }, void>({
      query: () => `/shift-closings/pending/count`,
      providesTags: () => [
        { type: 'ShiftClosing' as const, id: 'PENDING_COUNT' },
      ],
    }),

    // Get shift closing history with optional filters (admin/gerente only — 403 otherwise)
    getShiftClosingHistory: builder.query<ShiftClosingListDto[], {
      fromDate?: string;
      toDate?: string;
      status?: string;
    }>({
      query: ({ fromDate, toDate, status }) => ({
        url: `/shift-closings/history`,
        params: { fromDate, toDate, status },
      }),
      providesTags: (result) => [
        { type: 'ShiftClosing' as const, id: 'HISTORY' },
        ...(result || []).map(({ id }) => ({ type: 'ShiftClosing' as const, id })),
      ],
    }),

    // Get the current user's own shift closings (user + restaurant derived from the JWT;
    // safe for any authenticated employee, unlike the admin-only /history)
    getMyShiftClosings: builder.query<ShiftClosingListDto[], {
      fromDate?: string;
      toDate?: string;
      status?: string;
    }>({
      query: ({ fromDate, toDate, status }) => ({
        url: `/shift-closings/mine`,
        params: { fromDate, toDate, status },
      }),
      providesTags: (result) => [
        { type: 'ShiftClosing' as const, id: 'MINE' },
        ...(result || []).map(({ id }) => ({ type: 'ShiftClosing' as const, id })),
      ],
    }),

    // Create a new shift closing (employee submits their counts)
    createShiftClosing: builder.mutation<ShiftClosingDto, {
      cashRegisterId: string;
      data: CreateShiftClosingDto;
    }>({
      query: ({ cashRegisterId, data }) => ({
        url: `/shift-closings`,
        method: 'POST',
        params: { cashRegisterId },
        body: data,
      }),
      invalidatesTags: (result, error, { cashRegisterId }) => [
        { type: 'ShiftClosing' as const, id: 'LIST' },
        { type: 'ShiftClosing' as const, id: 'PENDING' },
        { type: 'ShiftClosing' as const, id: 'PENDING_COUNT' },
        { type: 'ShiftClosing' as const, id: 'MINE' },
        { type: 'CashRegister' as const, id: cashRegisterId },
        { type: 'CashRegister' as const, id: 'ACTIVE' },
      ],
    }),

    // Admin reviews and approves/rejects a shift closing
    reviewShiftClosing: builder.mutation<ShiftClosingDto, {
      id: string;
      data: ReviewShiftClosingDto;
    }>({
      query: ({ id, data }) => ({
        url: `/shift-closings/${id}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ShiftClosing' as const, id },
        { type: 'ShiftClosing' as const, id: 'PENDING' },
        { type: 'ShiftClosing' as const, id: 'PENDING_COUNT' },
        { type: 'ShiftClosing' as const, id: 'HISTORY' },
        { type: 'ShiftClosing' as const, id: 'MINE' },
        { type: 'CashRegister' as const, id: 'LIST' },
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'ALL_OPEN' },
      ],
    }),

    // Validate if user can create a shift closing
    validateCreateShiftClosing: builder.query<ShiftClosingValidationDto, string>({
      query: (cashRegisterId) => ({
        url: `/shift-closings/can-create`,
        params: { cashRegisterId },
      }),
      providesTags: (result, error, cashRegisterId) => [
        { type: 'ShiftClosing' as const, id: `validate-create-${cashRegisterId}` },
      ],
    }),

    // Validate if user can review a shift closing
    validateReviewShiftClosing: builder.query<ShiftClosingReviewValidationDto, string>({
      query: (id) => `/shift-closings/${id}/can-review`,
      providesTags: (result, error, id) => [
        { type: 'ShiftClosing' as const, id: `validate-review-${id}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetShiftClosingQuery,
  useGetShiftClosingsByCashRegisterQuery,
  useGetPendingShiftClosingsQuery,
  useGetPendingShiftClosingsCountQuery,
  useGetShiftClosingHistoryQuery,
  useGetMyShiftClosingsQuery,
  useCreateShiftClosingMutation,
  useReviewShiftClosingMutation,
  useValidateCreateShiftClosingQuery,
  useValidateReviewShiftClosingQuery,
} = shiftClosingApi;
